import React, { useState, useEffect, useCallback } from 'react';
import {
  ClipboardList, ChevronDown, ChevronUp, Send, ShieldAlert, Clock, AlertTriangle,
  CheckCircle2, FileText, History, XCircle,
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import type { OrgSpecialRequest, FormBuilderField } from '../../types';

/**
 * OrgPortalSpecialRequests — the Special Requests area shared by the
 * Referrer, Sponsor, and Employer portals (program 2, T2). It reads
 * ONLY the caller's own organizations' requests from
 * /api/org-portal/special-requests — applicant data can never appear
 * here. Submissions are versioned: correcting a rejected request
 * appends a new version and preserves the earlier ones.
 *
 * In restrictedOnly mode (used by the workspace restriction gate) it
 * shows only the outstanding BLOCKING requests requiring attention.
 */

const STATUS_STYLES: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600',
  Pending: 'bg-amber-100 text-amber-700',
  Viewed: 'bg-sky-100 text-sky-700',
  Submitted: 'bg-indigo-100 text-indigo-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-red-100 text-red-600',
  Overdue: 'bg-red-100 text-red-700',
  Expired: 'bg-red-100 text-red-700',
};

const SUBMITTABLE = ['Pending', 'Viewed', 'Rejected'];

function fmtDateTime(v?: string | null) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function fmtDate(v?: string | null) {
  if (!v) return '';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

interface OrgGroup {
  orgType: string;
  orgId: string;
  orgName: string;
  isMainUser: boolean;
  requests: (OrgSpecialRequest & { outstanding?: boolean })[];
}

interface PendingFile {
  filename: string;
  mimeType: string;
  base64Data: string;
  docName?: string | null;
  fieldId?: string | null;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OrgPortalSpecialRequests({
  restrictedOnly = false,
  heading,
}: {
  restrictedOnly?: boolean;
  heading?: string;
}) {
  const [orgs, setOrgs] = useState<OrgGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await safeFetch('/api/org-portal/special-requests');
      setOrgs(data.organizations || []);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load special requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleOrgs = orgs
    .map((org) => ({
      ...org,
      requests: restrictedOnly ? org.requests.filter((r) => r.outstanding) : org.requests,
    }))
    .filter((org) => org.requests.length > 0);

  const outstandingTotal = orgs.reduce(
    (n, org) => n + org.requests.filter((r) => r.outstanding).length, 0,
  );

  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="text-indigo-600" size={20} />
        <h2 className="text-lg font-bold text-slate-800">{heading || 'Special Requests'}</h2>
        {outstandingTotal > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-red-100 text-red-700">
            <ShieldAlert size={12} /> {outstandingTotal} outstanding
          </span>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Requests issued to your organization by the administration. Open a request to read its
        instructions, provide the information or documents asked for, and submit. If a submission
        is rejected, the reason is shown and you can correct and resubmit — your earlier
        submissions are kept on record.
      </p>

      {loading ? (
        <p className="text-sm text-slate-400 py-8 text-center">Loading special requests…</p>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      ) : visibleOrgs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
          <CheckCircle2 className="mx-auto text-emerald-500 mb-2" size={28} />
          <p className="text-sm text-slate-500">
            {restrictedOnly
              ? 'Nothing outstanding needs your attention right now.'
              : 'No special requests have been issued to your organization yet.'}
          </p>
        </div>
      ) : (
        visibleOrgs.map((org) => (
          <div key={`${org.orgType}/${org.orgId}`} className="mb-6">
            {orgs.length > 1 && (
              <h3 className="text-sm font-bold text-slate-600 mb-2 capitalize">
                {org.orgName} <span className="text-slate-400 font-medium">({org.orgType})</span>
              </h3>
            )}
            <div className="space-y-3">
              {org.requests.map((r) => (
                <RequestCard
                  key={r.requestId}
                  request={r}
                  expanded={expandedId === r.requestId}
                  onToggle={() => setExpandedId(expandedId === r.requestId ? null : r.requestId)}
                  onSubmitted={load}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function RequestCard({ request: r, expanded, onToggle, onSubmitted }: {
  request: OrgSpecialRequest & { outstanding?: boolean };
  expanded: boolean;
  onToggle: () => void;
  onSubmitted: () => void;
}) {
  const submittable = SUBMITTABLE.includes(r.status);
  const versions = Array.isArray(r.submissions) ? r.submissions : [];
  // Display-only state (owner directive): the stored status stays
  // 'Submitted', but a submitted request that in fact requires approval
  // is presented as "Pending Approval". r.requiresApproval is the
  // EFFECTIVE flag resolved by the portal API through the same helper
  // the restriction predicate uses — never the raw stamped field.
  const pendingApproval = r.status === 'Submitted' && r.requiresApproval === true;
  // Completion time = the LATEST submission version's timestamp,
  // falling back to the record's submittedAt mirror.
  const completedAt = versions.length
    ? versions[versions.length - 1].submittedAt
    : (r.submittedAt || null);

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-start gap-3 p-4 text-left cursor-pointer">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-800">{r.documentName}</span>
            <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${pendingApproval ? 'bg-amber-100 text-amber-700' : (STATUS_STYLES[r.status] || 'bg-slate-100 text-slate-500')}`}>
              {pendingApproval ? 'Pending Approval' : r.status}
            </span>
            {r.requestType === 'Form' && (
              <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-100 text-indigo-700">Form</span>
            )}
            {r.outstanding && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600">
                <ShieldAlert size={12} /> Blocks workspace access
              </span>
            )}
            {r.templateName && (
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{r.templateName}</span>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Issued {fmtDate(r.sentAt || r.createdAt)}
            {r.dueDate ? ` · Due ${fmtDate(r.dueDate)}` : ''}
            {pendingApproval && completedAt
              ? ` · Completed ${fmtDateTime(completedAt)}`
              : r.submittedAt ? ` · Last submitted ${fmtDateTime(r.submittedAt)}` : ''}
            {versions.length > 1 ? ` · ${versions.length} submissions` : ''}
          </p>
        </div>
        {expanded ? <ChevronUp size={18} className="text-slate-400 shrink-0" /> : <ChevronDown size={18} className="text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-4 space-y-4">
          {r.description && (
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Instructions</h4>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{r.description}</p>
            </div>
          )}
          {r.instructions && (
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Additional instructions</h4>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{r.instructions}</p>
            </div>
          )}
          {(r.requiredDocs || []).length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Required</h4>
              <ul className="text-sm text-slate-600 list-disc list-inside">
                {(r.requiredDocs || []).map((d: any, i: number) => (
                  <li key={i}>{typeof d === 'string' ? d : d?.name || ''}</li>
                ))}
              </ul>
            </div>
          )}

          {r.status === 'Rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-bold text-red-700 flex items-center gap-1.5">
                <XCircle size={15} /> This submission was rejected — please correct and resubmit
              </p>
              {r.rejectionReason && <p className="text-sm text-red-600 mt-1 whitespace-pre-wrap">{r.rejectionReason}</p>}
              <p className="text-xs text-red-400 mt-1">
                {r.reviewedByName ? `Reviewed by ${r.reviewedByName}` : 'Reviewed by an administrator'}
                {r.reviewedAt ? ` · ${fmtDateTime(r.reviewedAt)}` : ''}
              </p>
            </div>
          )}
          {r.status === 'Approved' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center gap-2">
              <CheckCircle2 size={15} /> Approved{r.reviewedByName ? ` by ${r.reviewedByName}` : ''}
              {r.approvedAt || r.reviewedAt ? ` · ${fmtDateTime(r.approvedAt || r.reviewedAt)}` : ''}
            </div>
          )}
          {r.status === 'Completed' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center gap-2">
              <CheckCircle2 size={15} /> Completed{r.completedAt ? ` · ${fmtDateTime(r.completedAt)}` : ''}
            </div>
          )}

          {versions.length > 0 && (
            <div>
              <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                <History size={12} /> Submission history
              </h4>
              <div className="space-y-2">
                {[...versions].reverse().map((v) => (
                  <div key={v.version} className="border border-slate-100 rounded-lg p-2.5 text-sm">
                    <p className="text-xs font-bold text-slate-500">
                      Version {v.version} · {fmtDateTime(v.submittedAt)}
                      {v.version === versions.length ? ' · Latest' : ''}
                    </p>
                    {v.responseValue && !v.formResponses && (
                      <p className="text-slate-600 whitespace-pre-wrap mt-1">{v.responseValue}</p>
                    )}
                    {(v.selectedValues || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(v.selectedValues || []).map((val, i) => (
                          <span key={i} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[11px] font-semibold">{val}</span>
                        ))}
                      </div>
                    )}
                    {v.formResponses && (
                      <dl className="mt-1 space-y-0.5">
                        {Object.entries(v.formResponses).map(([k, val]) => {
                          const label = (Array.isArray(r.formFields) ? r.formFields : []).find((f: any) => f && f.id === k)?.label || k;
                          return (
                            <div key={k} className="text-slate-600 text-xs">
                              <span className="font-semibold">{label}:</span> {Array.isArray(val) ? val.join(', ') : String(val ?? '')}
                            </div>
                          );
                        })}
                      </dl>
                    )}
                    {(v.responseFiles || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-2">
                        {(v.responseFiles || []).map((f, i) => (
                          <a key={i} href={f.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 underline">
                            <FileText size={12} /> {f.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {submittable ? (
            <SubmitForm request={r} onSubmitted={onSubmitted} />
          ) : pendingApproval ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-bold text-amber-800 flex items-center gap-1.5">
                <Clock size={15} /> Pending Review / Approval
              </p>
              <p className="text-sm text-amber-700 mt-1">
                You completed this request{completedAt ? ` on ${fmtDateTime(completedAt)}` : ''}.
                It is now pending review and approval by the administration, so there is nothing
                further to submit for it right now. When a decision is recorded, this request will
                show the outcome here — and if anything needs correcting, it will come back with
                the reason so you can fix and resubmit it.
              </p>
            </div>
          ) : r.status === 'Submitted' ? (
            <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
              Submitted and awaiting review. If anything needs correcting, the administration will
              return it here with the reason.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SubmitForm({ request: r, onSubmitted }: { request: OrgSpecialRequest; onSubmitted: () => void }) {
  const [text, setText] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [choice, setChoice] = useState('');
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const formFields: FormBuilderField[] = Array.isArray(r.formFields) ? r.formFields : [];
  const needsFiles = r.responseType === 'file_upload' || r.responseType === 'form';

  const onPickFiles = async (picked: FileList | null, tag?: { docName?: string; fieldId?: string }) => {
    if (!picked) return;
    const out: PendingFile[] = [];
    for (const file of Array.from(picked)) {
      out.push({
        filename: file.name,
        mimeType: file.type || 'application/octet-stream',
        base64Data: await readFileAsBase64(file),
        docName: tag?.docName || null,
        fieldId: tag?.fieldId || null,
      });
    }
    setFiles((prev) => [...prev, ...out]);
  };

  const submit = async () => {
    if (r.responseType === 'multiple_choice' && selectedChoices.length === 0) {
      notifyToast('error', 'Please select at least one option before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const body: any = { files };
      if (r.responseType === 'form') {
        body.formResponses = formValues;
      } else if (r.responseType === 'date') {
        body.responseText = dateValue;
      } else if (r.responseType === 'dropdown') {
        body.responseText = choice;
      } else if (r.responseType === 'multiple_choice') {
        body.selectedValues = selectedChoices;
      } else if (r.responseType === 'short_text' || r.responseType === 'long_text') {
        body.responseText = text;
      }
      await safeFetch(`/api/org-portal/special-requests/${r.requestId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      notifyToast('success', r.status === 'Rejected' ? 'Resubmitted — thank you' : 'Submitted — thank you');
      onSubmitted();
    } catch (err: any) {
      notifyToast('error', err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t border-slate-100 pt-3 space-y-3">
      <h4 className="text-sm font-bold text-slate-700">
        {r.status === 'Rejected' ? 'Correct and resubmit' : 'Your response'}
      </h4>

      {r.responseType === 'short_text' && (
        <input value={text} onChange={(e) => setText(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Type your response" />
      )}
      {r.responseType === 'long_text' && (
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Type your response" />
      )}
      {r.responseType === 'date' && (
        <input type="date" value={dateValue} onChange={(e) => setDateValue(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
      )}
      {r.responseType === 'dropdown' && (
        <select value={choice} onChange={(e) => setChoice(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
          <option value="">Select…</option>
          {(r.dropdownOptions || []).map((opt: string, i: number) => (
            <option key={i} value={opt}>{opt}</option>
          ))}
        </select>
      )}
      {r.responseType === 'multiple_choice' && (
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500">Select all that apply.</p>
          {(r.dropdownOptions || []).map((opt: string, i: number) => (
            <label key={i} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedChoices.includes(opt)}
                onChange={(e) => setSelectedChoices((prev) =>
                  e.target.checked ? [...prev, opt] : prev.filter((v) => v !== opt))}
                className="rounded border-slate-300"
              />
              {opt}
            </label>
          ))}
        </div>
      )}

      {r.responseType === 'form' && formFields.map((f) => (
        <div key={f.id}>
          <label className="text-xs font-medium text-slate-500 block mb-1">
            {f.label}{f.required ? ' *' : ''}
          </label>
          {f.type === 'long_text' ? (
            <textarea value={formValues[f.id] ?? ''} onChange={(e) => setFormValues({ ...formValues, [f.id]: e.target.value })} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
          ) : f.type === 'dropdown' || f.type === 'radio' ? (
            <select value={formValues[f.id] ?? ''} onChange={(e) => setFormValues({ ...formValues, [f.id]: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Select…</option>
              {(f.options || []).map((o: any, i: number) => {
                const label = typeof o === 'string' ? o : o.label || o.value || '';
                const value = typeof o === 'string' ? o : o.value || o.label || '';
                return <option key={i} value={value}>{label}</option>;
              })}
            </select>
          ) : f.type === 'checkbox' || f.type === 'yes_no' ? (
            <select value={formValues[f.id] ?? ''} onChange={(e) => setFormValues({ ...formValues, [f.id]: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              <option value="">Select…</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          ) : f.type === 'file_upload' ? (
            <input type="file" onChange={(e) => onPickFiles(e.target.files, { fieldId: f.id })} className="text-sm" />
          ) : (
            <input
              type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : 'text'}
              value={formValues[f.id] ?? ''}
              onChange={(e) => setFormValues({ ...formValues, [f.id]: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />
          )}
        </div>
      ))}

      {needsFiles && (
        <div>
          <label className="text-xs font-medium text-slate-500 block mb-1">Upload documents</label>
          <input type="file" multiple onChange={(e) => onPickFiles(e.target.files)} className="text-sm" />
          {files.length > 0 && (
            <ul className="mt-1 text-xs text-slate-500 space-y-0.5">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-1">
                  <FileText size={12} /> {f.filename}
                  <button onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-red-400 ml-1">Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button
        onClick={submit}
        disabled={submitting}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-dark disabled:opacity-50 w-full sm:w-auto justify-center"
      >
        <Send size={15} /> {submitting ? 'Submitting…' : r.status === 'Rejected' ? 'Resubmit' : 'Submit'}
      </button>
    </div>
  );
}
